import axios, { CancelTokenSource } from 'axios';
import { ___API_BASE_URL, __API_URL_MAP } from '@/const';
import { commonStore } from '@/stores/quick-order';
import { storageHelper } from '@/utils';
import { Modal, login } from '@/components';
import Cookies from 'js-cookie';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import {
  InternetErrorException,
  LoginFailException,
} from "@/const/constException";

const version = '3.11.1';

// widget 模式下从 <script src> 的 hostname 自动推断 env，并回写到
// window.__WEB_SITE_APP_CONFIG.appConfig.env。这样 sigma 等模板不必硬编码 env /
// orgId — 只要切 widget script 域名即切环境，配合 appConfig.orgIdByEnv 实现
// "一份 HTML 跑所有环境"。
// host 已显式设置 env / apiBaseUrl 时不覆盖；Next.js 主项目内 document.currentScript
// 通常拿不到 src，会安静跳过、不影响主项目行为。
if (typeof window !== "undefined" && typeof document !== "undefined") {
  const cfg = (window as any).__WEB_SITE_APP_CONFIG?.appConfig;
  const cs = document.currentScript as HTMLScriptElement | null;
  if (cfg && cs && cs.src && !cfg.env && !cfg.apiBaseUrl) {
    try {
      const host = new URL(cs.src).hostname;
      const inferred =
        host === "order.peblla.com" ? "prod" :
        host.indexOf("order-demo") === 0 ? "demo" :
        host.indexOf("order-test") === 0 ? "test" :
        host.indexOf("order-dev")  === 0 ? "dev"  :
        undefined;
      if (inferred) cfg.env = inferred;
    } catch (e) { /* malformed URL — ignore */ }
  }
}

export interface ResponseData<T = any> {
  more?: Record<string, T>;
  result?: Record<string, T>;
  status?: Record<string, T>;
  pageInfo?: Record<string, T>;
}
export enum RequestMethod {
  GET = "get",
  POST = "post",
  PUT = "put",
  DELETE = "delete",
}
const methodParamsTypeMap = new Map([
  [RequestMethod.GET, "params"],
  [RequestMethod.PUT, "data"],
  [RequestMethod.POST, "data"],
  [RequestMethod.DELETE, "data"],
]);

type Headers = Record<string, string | undefined> | null | undefined;
type BaseUrl = string | null | undefined;
interface RequestParams {
  baseUrl?: BaseUrl;
  headers?: Headers;
  authentication?: boolean;
}
export class Request {
  private authentication = true;
  private readonly client: AxiosInstance;
  private readonly methodParamsTypeMap = methodParamsTypeMap;
  private readonly baseUrl: BaseUrl = null;
  private headers: Headers = {};

  private isLogin = false;
  private tipsCount = 0;
  private cancelTokens: Map<string, CancelTokenSource>;

  constructor(params: RequestParams = {}) {
    this.baseUrl = params.baseUrl;
    if (params.authentication !== undefined) {
      this.authentication = params.authentication;
    }
    const sessionid = Cookies.get("__session_id");
    const tid = Cookies.get("__tid");
    const orgId = Cookies.get("__orgId");
    const channel = Cookies.get("__channel");

    if (sessionid && this.authentication) {
      this.headers = { ...this.headers, Sessionid: sessionid };
    }

    if (typeof params.headers === "object") {
      this.headers = { ...this.headers, ...params.headers };
    }

    // widget 嵌入第三方页时，bundle 自带的 ___API_BASE_URL 锁死为打包时的环境（dev）。
    // 允许 host 通过 window.__WEB_SITE_APP_CONFIG.appConfig 覆盖：
    //   - apiBaseUrl: 显式 URL（强覆盖）
    //   - env: 'dev' | 'test' | 'demo' | 'prod'（按 __API_URL_MAP 查表）
    const winAppConfig = typeof window !== "undefined"
      ? (window as any)?.__WEB_SITE_APP_CONFIG?.appConfig
      : undefined;
    const winApiBaseUrl =
      winAppConfig?.apiBaseUrl ||
      (winAppConfig?.env ? __API_URL_MAP.get(winAppConfig.env) : undefined);

    this.client = axios.create({
      baseURL: this.baseUrl || winApiBaseUrl || ___API_BASE_URL,
      timeout: 20000,
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        Accept: "application/json;charset=UTF-8",
        "Cache-Control": "no-cache",
        orgId: orgId,
        tid: tid,
        channel: "Web",
        "x-app-platform": "web",
        "x-app-product": "order",
        "x-app-version": version,
        "x-project-version": version,
        ...this.headers,
      },
    });
    this.cancelTokens = new Map();
  }

  private cancelPreviousRequest (requestType?: any, cancelPrevious = false) {
    if (!cancelPrevious) return

    const cancelToken = this.cancelTokens.get(requestType);

    if (cancelToken) {
      cancelToken.cancel('重复请求')
      this.cancelTokens.delete(requestType);
    }
  }

  public create(params: RequestParams = {}): Request {
    return new Request(params);
  }

  private tips = (text?: string) => {
    if (process.browser) {
      const that = this;

      if (that.tipsCount > 0) {
        return;
      }
      Modal.info({
        title: "Oops!",
        titleStyle: { color: "var(--color-primary)" },
        content: <div dangerouslySetInnerHTML={{ __html: text || "" }}></div>,
        className: "overall_situation_modal_tips",
        closable: false,
        footerProps: { ghost: false },
        onCancel: function () {
          that.tipsCount = 0;
        },
      });

      that.tipsCount++;
    }
  };

  private async sendRequest<T>(
    method: RequestMethod,
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    otherParams: Record<string, any> = { isTips: true, cancelPrevious: true }
  ): Promise<ResponseData<T>> {

    // const cancelSource = axios.CancelToken.source();
    this.cancelPreviousRequest(url, otherParams?.cancelPrevious);
    const newCancelToken = axios.CancelToken.source();

    if (otherParams?.cancelPrevious) {
      this.cancelTokens.set(url, newCancelToken);
    }

    let requestConfig: AxiosRequestConfig = {
      method,
      url,
      cancelToken: this.cancelTokens.get(url)?.token,
      headers: {
        ...this.headers,
        ...headers,
      },
    };

    if (data) {
      const requestKey = this.methodParamsTypeMap.get(method);
      if (requestKey) {
        requestConfig = { ...requestConfig, [requestKey]: data };
      }
    }

    let response: AxiosResponse<ResponseData<T>>;

    try {
      response = await this.client.request<ResponseData<T>>(requestConfig);
    } catch (error: any) {
      
      if (error?.message === "重复请求" && otherParams?.cancelPrevious) {
        // this.tips(error?.message)
        console.error(error)
        throw Error(error?.message)
      }

      if (axios.isCancel(error)) {
        if (otherParams?.isTips) {
          this.tips(InternetErrorException?.content);
        }
        throw new Error("Request canceled");
      } else {
        if (otherParams?.isTips) {
          this.tips(InternetErrorException?.content);
        }
        console.error(
          "Request failed with error message:",
          error.message || ""
        );
        throw new Error("Request failed");
      }
    }
    // finally {
    //   if (otherParams.cancelPrevious) {
    //     this.cancelTokens.delete(url);
    //   }
    // }

    const { data: responseData } = response;

    if (Number(responseData?.status?.code) === 93100) {
      // this.cancelSource?.cancel('登录失效，请重新登录');
      if (!this.isLogin) {
        commonStore.updateState({ 'memberInfo': null });
        commonStore.updateState({ 'isLogin': false });
        storageHelper.setItem('__member_info', {});
        storageHelper.setItem('__delivery_address', null);
        storageHelper.setItem('__delivery_address_default', null);

        Modal.info(
          {
            className: 'overall_situation_modal_tips',
            title: LoginFailException?.title,
            titleStyle: { color: "var(--color-primary)" },
            content: LoginFailException?.content,
            closable: false,
            maskClosable: false,
            footerProps: { ghost: false },
            onOk: () => {
              login({
                intercept: false,
                onCancel: () => {
                  this.isLogin = false;
                }
              });
            }
          }
        );
        this.isLogin = true;
      }

      throw new Error("登录失效，请重新登录！");
    }
    if (Number(responseData?.status?.code) !== 0) {
      if (typeof otherParams.isTips === "boolean" && otherParams.isTips) {
        this.tips(responseData?.status?.msg as string);
        throw responseData?.status;
      } else if (Array.isArray(otherParams.isTips)) {
        if (!otherParams?.isTips?.includes(responseData?.status?.code)) {
          this.tips(responseData?.status?.msg as string);
          throw responseData?.status;
        }
      }
    }

    return responseData as ResponseData<T>;
  }

  public async get<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    otherParams: Record<string, any> = { isTips: true, cancelPrevious: false }
  ): Promise<ResponseData<T>> {
    return this.sendRequest(RequestMethod.GET, url, data, headers, otherParams);
  }
  public async post<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    otherParams: Record<string, any> = { isTips: true, cancelPrevious: false }
  ): Promise<ResponseData<T>> {
    return this.sendRequest(
      RequestMethod.POST,
      url,
      data,
      headers,
      otherParams
    );
  }
  public async put<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    otherParams: Record<string, any> = { isTips: true, cancelPrevious: false }
  ): Promise<ResponseData<T>> {
    return this.sendRequest(RequestMethod.PUT, url, data, headers, otherParams);
  }
  public async delete<T>(
    url: string,
    data?: Record<string, unknown>,
    headers?: Record<string, string>,
    otherParams: Record<string, any> = { isTips: true, cancelPrevious: false }
  ): Promise<ResponseData<T>> {
    return this.sendRequest(
      RequestMethod.DELETE,
      url,
      data,
      headers,
      otherParams
    );
  }
}

export default new Request();
